import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import AIReviewDetailTemplate from "../../components/templates/AIReviewDetailTemplate";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import AIReviewDetailAdminViewerTemplate from "components/templates/AIReviewDetailTemplate/AdminViewer";
import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { Button } from "components/atoms";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { eventLog } from "utils/AmplitudeUtil";
import { useAIReviewPin } from "hooks/useAIReviewPin";
import { useAIReviewMemo } from "hooks/useAIReviewMemo";
import { useAIReviewDelete } from "hooks/useAIReviewDelete";
import { useAIReviewStream } from "hooks/useAIReviewStream";
import MemoEditModal from "components/molecules/MemoEditModal";
import { setAIReview } from "../../store/actions";
import {
  AIReview,
  FeedbackItem,
  AIReviewFeedback,
  TextConsultationContext,
} from "../../common/models/types";
import {
  retryAIReviewLLM,
  fetchAIReviewById,
  createAIReviewFeedback,
  updateAIReviewFeedback,
  getAIReviewFeedback,
} from "../../api/aiReviewService";
import {
  fetchConsultationRecords,
  fetchConsultationRecordById,
  createTextConsultationRecord,
  updateTextConsultationRecord,
} from "../../api/consultationService";
import { fetchPatientConsultationRecords } from "../../store/middleware";
import { AppDispatch } from "../../store";
import TextRecordSlide from "components/templates/TextRecordSlide";
import {
  isTextRecordOpen,
  textRecordEnvKey,
  flush as flushTextRecordQueue,
} from "components/templates/TextRecordSlide/textRecordSaveQueue";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { HybridStreamManager } from "../../services/HybridStreamManager";
import { FeedbackProvider, Rating } from "../../contexts/FeedbackContext";

// 슬라이드 고유 key용 모듈 카운터(선례 openDoseReviewTestSlide).
let textRecordOpenSeq = 0;

const AIReviewDetailPage: React.FC = () => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { togglePin } = useAIReviewPin();
  const { updateMemo } = useAIReviewMemo();
  const { deleteReview } = useAIReviewDelete();
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 피드백 탭 상태 (Page 레벨에서 관리)
  const [activeFeedbackTab, setActiveFeedbackTab] = useState<string | undefined>();

  // 새로고침 후 별점만 한 상태에서도 상세 입력 가능하도록, feedbackId가 있으면 상세 데이터 fetch
  // tri-state: undefined(fetch 시도 전·진행 중) / null(fetch 실패 또는 결과 없음) / 객체(성공)
  const [existingFeedback, setExistingFeedback] = useState<AIReviewFeedback | null | undefined>(undefined);

  const { user } = useSelector((state: RootState) => state.user);

  const isAdmin = user?.roles.includes("admin");

  // AI 재분석 이중 발사 방지: 동기 재진입 락 (retry-llm 전용)
  const isRetryInFlightRef = useRef<boolean>(false);

  const review = useSelector((state: RootState) =>
    reviewId ? state.data.reviews[reviewId] : null
  );
  // 상담 체크리스트 머리 줄 환자 맥락(사이드바와 같은 store 환자 — 결정 20). 없으면 보고서의 환자 스냅샷.
  const checklistPatient = useSelector((state: RootState) =>
    review?.patientId ? state.data.patients[review.patientId] : null
  ) ?? review?.patientSnap ?? null;

  // Stream 자동 관리 - Redux 업데이트와 Toast 알림은 StreamManager가 처리.
  // 끊김 상태·[새로고침]은 화면 로컬 상태로 받아 보고서 화면에 그대로 내려보낸다.
  const disconnection = useAIReviewStream(reviewId ? Number(reviewId) : null);

  // 새로고침 등으로 feedbackId가 있는 상태로 진입한 경우 상세 데이터 fetch
  useEffect(() => {
    setExistingFeedback(undefined);
    if (review?.feedbackId && typeof review.feedbackId === "number") {
      getAIReviewFeedback(review.id)
        .then(setExistingFeedback)
        .catch((error) => {
          // 실패해도 약사가 상세 입력 못 하는 영구 차단 피하기 위해 null로 마킹
          log(LogLevel.ERROR, "피드백 상세 조회 실패:", error);
          setExistingFeedback(null);
        });
    }
  }, [review?.id, review?.feedbackId]);

  // ── 상담 체크리스트(텍스트 기록) — 보고서 화면 입구(spec 설계 결정 8·13·16·20) ──────────
  const [hasTextRecord, setHasTextRecord] = useState(false);
  const [textRecordStatus, setTextRecordStatus] = useState<"loading" | "ready" | "error">("loading");
  const textRecordIdRef = useRef<number | null>(null); // 이 보고서의 텍스트 기록 id(있으면)
  const recordQuerySeqRef = useRef(0); // A→B 이동 시 늦은 응답 무시
  const lastSlideKeyRef = useRef<string | null>(null); // 재열기 전 큐 flush 대상

  const loadTextRecordPresence = useCallback(async () => {
    if (!review?.id || !review.patientId) return;
    const rid = review.id;
    const seq = ++recordQuerySeqRef.current;
    setTextRecordStatus("loading");
    const res = await fetchConsultationRecords({
      patientId: review.patientId,
      status: "completed",
      reviewId: rid,
      source: "text",
      limit: 1,
    });
    if (seq !== recordQuerySeqRef.current) return; // 더 늦은 조회가 이미 떴다
    if (res.error !== 0 || !res.data) {
      setTextRecordStatus("error");
      ToastManager.show({ content: "체크리스트를 확인하지 못했어요.", duration: 3000 });
      return;
    }
    // 응답의 reviewId 일치 확인(A→B 이동 시 재마운트 안 됨)
    const item = (res.data.records || []).find((r) => r.reviewId === rid && r.source === "text");
    textRecordIdRef.current = item?.id ?? null;
    setHasTextRecord(!!item);
    setTextRecordStatus("ready");
  }, [review?.id, review?.patientId]);

  useEffect(() => {
    loadTextRecordPresence();
  }, [loadTextRecordPresence]);

  const afterTextRecordSave = useCallback(() => {
    loadTextRecordPresence();
    if (review?.patientId) dispatch(fetchPatientConsultationRecords(review.patientId));
  }, [loadTextRecordPresence, review?.patientId, dispatch]);

  const openTextRecord = useCallback(async () => {
    if (!review?.id || !review.patientId) return;
    const rid = review.id;
    const recordId = textRecordIdRef.current;
    // 열림 판정은 입력 모드("edit") 하나로 — 첫 저장으로 recordId가 생겨도 같은 열림으로 본다(결함 9).
    const env = textRecordEnvKey(review.patientId, rid, "edit");
    if (isTextRecordOpen(env)) return; // 같은 환자·보고서·입력 모드로 이미 열림 → 무시

    // 닫았다가 다시 여는 경우 이전 저장 큐가 빌 때까지 기다린다(옛 내용 경쟁 차단).
    if (lastSlideKeyRef.current) await flushTextRecordQueue(lastSlideKeyRef.current);

    let initialContext: TextConsultationContext | null = null;
    if (recordId) {
      const detail = await fetchConsultationRecordById(recordId);
      if (detail.error !== 0 || !detail.data) {
        ToastManager.show({
          content: detail.status === 404 ? "기록을 찾을 수 없어요." : "기록을 불러오지 못했어요.",
          duration: 3000,
        });
        return;
      }
      initialContext = (detail.data.context as TextConsultationContext) ?? null;
    }

    const key = `text-record:${recordId ?? "new"}:${rid}:${++textRecordOpenSeq}`;
    lastSlideKeyRef.current = key;
    SlideManager.show({
      body: (
        <TextRecordSlide
          key={key}
          slideKey={key}
          patientId={review.patientId}
          reviewId={rid}
          recordId={recordId}
          review={review}
          initialContext={initialContext}
          patient={checklistPatient}
          onCreate={createTextConsultationRecord}
          onUpdate={updateTextConsultationRecord}
          onCreated={() => {
            ToastManager.show({ content: "체크리스트를 저장했어요.", duration: 2500 });
            afterTextRecordSave();
          }}
          onClose={afterTextRecordSave}
        />
      ),
      width: "560px",
      noBackdrop: true,
      needCloseButton: false,
    });
  }, [review, afterTextRecordSave, checklistPatient]);

  // 피드백 별점 제출 핸들러 (Page 레벨 - 부수효과 담당)
  const handleSubmitRating = useCallback(
    async (aiReviewId: number, rating: Rating): Promise<{ feedbackId?: number }> => {
      try {
        const response = await createAIReviewFeedback(aiReviewId, rating);

        // UI 부수효과: Toast 메시지
        ToastManager.show({ content: "피드백 감사합니다!", duration: 2000 });

        // UI 부수효과: 피드백 탭 자동 활성화
        setActiveFeedbackTab("피드백");

        // 이벤트 로깅
        eventLog("ai_review_feedback_submitted", {
          reviewId: aiReviewId,
          rating,
        });

        return { feedbackId: response.data?.id };
      } catch (error) {
        log(LogLevel.ERROR, "피드백 제출 실패:", error);
        ToastManager.show({
          content: "피드백 제출에 실패했습니다. 다시 시도해주세요.",
          duration: 3000,
        });
        throw error;
      }
    },
    []
  );

  // 피드백 상세 의견 추가 핸들러 (Page 레벨 - 부수효과 담당)
  const handleAddDetails = useCallback(
    async (
      aiReviewId: number,
      data: {
        likes?: FeedbackItem[];
        dislikes?: FeedbackItem[];
        comment?: string;
      }
    ): Promise<void> => {
      try {
        const response = await updateAIReviewFeedback(aiReviewId, {
          likes: data.likes,
          dislikes: data.dislikes,
          comment: data.comment,
        });

        // UI 부수효과: Toast 메시지
        ToastManager.show({
          content: "피드백을 제출해주셔서 감사합니다!",
          duration: 2000,
        });

        // 이벤트 로깅
        eventLog("ai_review_feedback_details_added", {
          reviewId: aiReviewId,
          likesCount: data.likes?.length || 0,
          dislikesCount: data.dislikes?.length || 0,
          hasComment: !!data.comment,
        });

        // 3초 후 리뷰 데이터 갱신 (feedbackId 포함)
        // reducer가 기존 review와 병합하므로 id와 feedbackId만 전달
        if (response.data?.id && review) {
          const feedbackId = response.data.id;
          const reviewId = review.id;
          setTimeout(() => {
            dispatch(setAIReview({ id: reviewId, feedbackId } as AIReview));
            log(LogLevel.INFO, "AI리뷰 feedbackId 업데이트 성공", { reviewId });
          }, 3000);
        }
      } catch (error) {
        log(LogLevel.ERROR, "피드백 상세 추가 실패:", error);
        ToastManager.show({
          content: "피드백 제출에 실패했습니다. 다시 시도해주세요.",
          duration: 3000,
        });
        throw error;
      }
    },
    [review, dispatch]
  );

  if (!review) {
    return null;
  }

  const handleRefresh = async () => {
    if (!review) return;

    try {
      WaitingManager.show({ message: "리뷰 데이터를 새로고침 중..." });
      const result = await fetchAIReviewById(review.id);

      if (!result.error && result.data) {
        dispatch(setAIReview(result.data));
        // SlideManager 팝업을 새 데이터로 다시 열기
        SlideManager.show({
          body: <AIReviewDetailAdminViewerTemplate review={result.data} onDeleteReview={onDeleteReport} onRetryLLM={handleRetryLLM} onRefresh={handleRefresh} />,
          width: "800px",
        });
        ToastManager.show({ content: "리뷰 데이터가 새로고침되었습니다.", duration: 2000 });
        eventLog("AI 리뷰 새로고침", { reviewId: review.id, patientId: review.patientId });
      } else {
        ToastManager.show({ content: "새로고침에 실패했습니다.", duration: 3000 });
      }
    } catch (error) {
      console.error("Failed to refresh review:", error);
      ToastManager.show({ content: "새로고침 중 오류가 발생했습니다.", duration: 3000 });
    } finally {
      WaitingManager.hide();
    }
  };

  const handleRetryLLM = () => {
    ConfirmManager.show({
      title: "AI 재분석",
      body: "기존의 분석결과는 사라집니다. 계속하시겠습니까?",
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => {
            ConfirmManager.hide();
          }}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            // 동기 재진입 락: await 이전에 잡아 재분석 이중 발사 차단
            if (isRetryInFlightRef.current) return;
            isRetryInFlightRef.current = true;

            ConfirmManager.hide();
            try {
              WaitingManager.show({ message: "AI 재분석을 진행하는 중..." });

              const result = await retryAIReviewLLM(review.id);
              
              if (!result.error && result.data) {
                // Redux store 업데이트 (재분석 시작 상태 반영)
                dispatch(setAIReview(result.data));
                
                // 스트림 재시작하여 실시간 업데이트 받기
                const streamManager = HybridStreamManager.getInstance();
                streamManager.restartStream(review.id);
                
                // 팝업 닫기
                SlideManager.hide();
                
                eventLog("AI 리뷰 재분석 시작", { reviewId: review.id, patientId: review.patientId });
              } else {
                eventLog("AI 리뷰 재분석 실패", { reviewId: review.id, patientId: review.patientId, error: result.message });
              }
            } catch (error) {
              console.error("Failed to retry LLM:", error);
              eventLog("AI 리뷰 재분석 오류", { reviewId: review.id, patientId: review.patientId, error: error instanceof Error ? error.message : String(error) });
            } finally {
              isRetryInFlightRef.current = false;
              WaitingManager.hide();
            }
          }}
        >
          재분석
        </Button>,
      ],
      showBackdrop: true,
      backdropClose: true,
      showCloseButton: false,
    });
  };

  const onShowAdminDetail = () => {
    if (!isAdmin) {
      return;
    }
    eventLog("분석결과 관리자 상세보기", { reviewId, patientId: review.patientId });

    SlideManager.show({
      body: <AIReviewDetailAdminViewerTemplate review={review} onDeleteReview={onDeleteReport} onRetryLLM={handleRetryLLM} onRefresh={handleRefresh} />,
      width: "800px",
    });
  };

  const onTogglePin = () => {
    log(LogLevel.INFO, "AIReviewDetailPage:onTogglePin", review);
    if (review?.id) {
      togglePin(review.id);
    }
  };

  const onEditMemo = () => {
    setShowMemoModal(true);
  };

  const handleMemoModalSubmit = async (memo: string) => {
    if (review?.id) {
      await updateMemo(review.id, memo);
    }
    setShowMemoModal(false);
  };

  const onDeleteReport = () => {
    ConfirmManager.show({
      title: "보고서 삭제",
      body: "이 보고서를 삭제하시겠습니까? 삭제한 보고서는 마이페이지에서 확인할 수 있습니다.",
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => {
            ConfirmManager.hide();
          }}
        >
          취소
        </Button>,
        <Button
          key="delete"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={doDeleteReview}
        >
          삭제
        </Button>,
      ],
      showBackdrop: true,
      backdropClose: true,
      showCloseButton: false,
    });
  };

  const doDeleteReview = async () => {
    log(LogLevel.INFO, "AIReviewDetailPage:doDeleteReview", review);
    if (!review) return;
    
    WaitingManager.show({ message: "보고서를 삭제 중입니다..." });
    ConfirmManager.hide();
    
    const success = await deleteReview(review.id, review.patientId);
    
    if (success) {
      eventLog("AI 보고서 삭제", { reviewId: review.id, patientId: review.patientId });
    } else {
      eventLog("AI 보고서 삭제 실패", { reviewId: review.id, patientId: review.patientId });
    }
    
    WaitingManager.hide();
  };

  const handleReviewUpdate = (updatedReview: AIReview) => {
    // Redux store 업데이트
    dispatch(setAIReview(updatedReview));
    log(LogLevel.INFO, "AI리뷰 업데이트 성공", { reviewId: updatedReview.id });
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    eventLog("AI 보고서 전체화면 토글", {
      reviewId: review.id,
      patientId: review.patientId,
      isFullscreen: !isFullscreen
    });
  };

  return (
    <FeedbackProvider
      review={review}
      existingFeedback={existingFeedback}
      onSubmitRating={handleSubmitRating}
      onAddDetails={handleAddDetails}
    >
      <AIReviewDetailTemplate
        review={review}
        onAdminDetail={isAdmin && onShowAdminDetail}
        onTogglePin={onTogglePin}
        onEditMemo={onEditMemo}
        onDeleteReport={onDeleteReport}
        onReviewUpdate={handleReviewUpdate}
        isFullscreen={isFullscreen}
        onToggleFullscreen={handleToggleFullscreen}
        activeFeedbackTab={activeFeedbackTab}
        onFeedbackTabChange={setActiveFeedbackTab}
        disconnection={disconnection}
        onOpenTextRecord={openTextRecord}
        hasTextRecord={hasTextRecord}
        textRecordStatus={textRecordStatus}
        onRetryTextRecord={loadTextRecordPresence}
      />

      {review && (
        <MemoEditModal
          isOpen={showMemoModal}
          onClose={() => setShowMemoModal(false)}
          onSubmit={handleMemoModalSubmit}
          title="보고서 메모 수정"
          initialMemo={review.memo || ""}
        />
      )}
    </FeedbackProvider>
  );
};

export default AIReviewDetailPage;
